import {
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { getUserFullName } from '@duke-hq/lib';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Trash2 } from 'lucide-react';
import { FiMoreHorizontal } from 'react-icons/fi/index.js';
import type { StudentType } from './StudentDetailPage';
import { testIds } from './testIds';
import { useCheckPermission } from '../hooks/useCheckPermission';

const DELETED_EMAIL_SUFFIX = '.test';

function withDeletedEmailSuffix(email: string | null): string {
  if (email == null) {
    return DELETED_EMAIL_SUFFIX;
  }

  return email.endsWith(DELETED_EMAIL_SUFFIX) ? email : `${email}${DELETED_EMAIL_SUFFIX}`;
}

export function StudentActionsMenu({ student }: { student: StudentType }): React.ReactNode {
  const canDeleteStudentUsers = useCheckPermission('canDeleteStudentUsers');
  const confirmDialog = useDisclosure();
  const toast = useToast();
  const utils = trpcAdmin.useUtils();
  const deletedEmail = withDeletedEmailSuffix(student.email);

  const deleteStudentUser = trpcAdmin.students.deleteStudentUser.useMutation({
    onSuccess: () => {
      utils.students.byId.invalidate({ id: student.id });
      confirmDialog.onClose();
      toast({ title: t`Student deleted`, status: 'success' });
    },
    onError: (error) => {
      toast({
        title: t`Failed to delete student`,
        description: error.message,
        status: 'error',
      });
    },
  });

  if (!canDeleteStudentUsers || student.deleted_at != null) {
    return null;
  }

  return (
    <>
      <Menu>
        <MenuButton
          as={IconButton}
          aria-label={t`Options`}
          icon={<FiMoreHorizontal />}
          variant="ghost"
          data-testid={testIds.studentActionsMenu}
        />
        <MenuList>
          <MenuItem
            icon={<Trash2 width={16} height={16} />}
            onClick={confirmDialog.onOpen}
            data-testid={testIds.deleteStudentButton}
          >
            <Trans>Delete student</Trans>
          </MenuItem>
        </MenuList>
      </Menu>

      <ConfirmationDialog
        isOpen={confirmDialog.isOpen}
        onClose={confirmDialog.onClose}
        title={<Trans>Delete student</Trans>}
        body={
          <VStack align="start" spacing={2} data-testid={testIds.deleteStudentDialog}>
            <Text>
              <Trans>
                Delete{' '}
                {getUserFullName({ first_name: student.first_name, last_name: student.last_name })}?
              </Trans>
            </Text>
            <Text>
              <Trans>
                The student's email address will be updated to {deletedEmail}, their phone number
                will be deleted permanently, their devices list will be cleared, they can no longer
                sign in and they will be signed out of the student app.
              </Trans>
            </Text>
          </VStack>
        }
        onConfirm={() => deleteStudentUser.mutate({ id: student.id })}
        confirmLabel={<Trans>Delete</Trans>}
        variant="destructive"
        isLoading={deleteStudentUser.isPending}
      />
    </>
  );
}
